/* eslint-disable react-hooks/set-state-in-effect -- email-step effects synchronize form and submission workflow state. */
import React, { useId, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Button from 'reactstrap/lib/Button';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

// Utils
import {
  VALIDATORS,
  onEmailStepContinue,
  getStepCtaAriaLabel,
} from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-utils';
import { EventToolbox } from 'client/utils/event-toolbox';
import { logger } from 'client/utils/logger/logger';

// Hooks
import { usePrevious } from 'site-modules/shared/hooks/use-previous';

// Constants
import { TrackingConstant } from 'client/tracking/constant';
import { EMAIL_STEP_TRACKING_ID, INSTANT_OFFER, EMAIL } from 'site-modules/shared/constants/appraisal/appraisal';

// Components
import { EdmundsLogoText } from 'site-modules/shared/components/logos/edmunds-logo-text';
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import { FieldError } from 'site-modules/shared/components/field-error/field-error';
import { EmailFormDisclaimer } from 'site-modules/shared/components/appraisal/email-form-disclaimer/email-form-disclaimer';
import { Spinner } from 'site-modules/shared/components/spinner/spinner';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { useTradeInAppraiserContext } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-context';

import './email-step.scss';

const NEXT_STEP_ID = INSTANT_OFFER;

const onEmailDataReady = async emailProps => {
  try {
    await onEmailStepContinue(emailProps);
  } catch (error) {
    logger.error(`Email step error - ${error}`);
  }
};

export const EmailStepUI = ({
  validationErrors = {},
  validate,
  fieldRef,
  isStepActive = false,
  fields = {},
  userEmail = '',
  updateIdmData,
  isAuthenticated = false,
  isEmailDataReady = false,
  stepCounter,
  nextStepTitle = '',
}) => {
  const { changeStep, toggleStepDone, setAdditionalStepsData: updateStepsState } = useTradeInAppraiserContext();
  const [email, setEmail] = useState(userEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const uniqInputId = useId();
  const emailErrorFieldId = `email-input-${uniqInputId}-error`;
  const { email: emailError } = validationErrors;
  const ctaText = `See Your Car's Value`;
  const defaultEmailValue = email || userEmail;
  const prevIsStepActive = usePrevious(isStepActive);

  useEffect(() => {
    if (isStepActive) {
      toggleStepDone(false, EMAIL);
      setIsSubmitting(false);
      setIsSubmitted(false);
    }
  }, [isStepActive, toggleStepDone]);

  useEffect(() => {
    if (isStepActive && !prevIsStepActive && defaultEmailValue) {
      fields.email?.setValue(defaultEmailValue);
      setEmail(defaultEmailValue);
    }
  }, [defaultEmailValue, fields.email, isStepActive, prevIsStepActive]);

  const doesNotHaveEmail = !userEmail;

  useEffect(() => {
    if (isEmailDataReady) {
      updateStepsState({ isEmailDataReady: false });

      if (isSubmitting) {
        onEmailDataReady({
          isAuthenticated,
          doesNotHaveEmail,
          email,
          updateIdmData,
        });

        setIsSubmitting(false);
        setIsSubmitted(true);
      }
    }
  }, [doesNotHaveEmail, email, isEmailDataReady, isAuthenticated, isSubmitting, updateIdmData, updateStepsState]);

  const onEmailFormFieldFocus = useCallback(() => {
    if (isSubmitted) {
      setIsSubmitted(false);
    }
  }, [isSubmitted]);

  const onChange = useCallback(
    e => {
      setEmail(e.target.value);
    },
    [setEmail]
  );

  /**
   * Sets ref for certain field
   * @param field
   */
  const inputRef = field => {
    if (!field) {
      return undefined;
    }

    return fieldRef(field.props.name, field);
  };

  const handleSubmitEmail = useCallback(
    async e => {
      e.preventDefault();
      e.stopPropagation();
      const isEmailValid = validate();

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
        event_data: {
          action_name: TrackingConstant.ACTION_SIGN_UP,
          subaction_name: TrackingConstant.EMAIL_VALIDATION,
          action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          creative_id: EMAIL_STEP_TRACKING_ID,
          value: isEmailValid ? 'success' : 'fail',
        },
      });

      if (isEmailValid) {
        setIsSubmitting(true);

        EventToolbox.fireTrackAction({
          event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
          event_data: {
            action_name: TrackingConstant.ACTION_SIGN_UP,
            subaction_name: TrackingConstant.SUBMIT_SIGN_UP_EMAIL,
            action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
            action_category: TrackingConstant.USER_ACTION_CATEGORY,
            creative_id: EMAIL_STEP_TRACKING_ID,
            value: ctaText,
          },
        });

        updateStepsState(
          {
            emailFromEmailStep: email,
            emailReportStatus: '', // reset email report status.
          },
          () => {
            changeStep({ stepId: NEXT_STEP_ID });
          }
        );
      }
    },
    [validate, ctaText, updateStepsState, email, changeStep]
  );

  if (!isStepActive) return null;

  const isValid = !emailError;

  return (
    <div className="email-step" data-tracking-parent={EMAIL_STEP_TRACKING_ID}>
      <EdmundsLogoText className="edmunds-logo-text d-block mb-1_5" />
      <h3 className="size-20 fw-bold text-cool-gray-10 mb-0_5">Where should we send your Appraisal Report?</h3>
      <p className="size-16 text-cool-gray-30 mb-1_5">
        Last step! You&apos;ll see your car&apos;s value on the next page and get a copy to keep in your inbox.
      </p>
      <div>
        <Row>
          <Col xs={12} md={7} xl={5} className="pos-r">
            <FormField
              id={`email-input-${uniqInputId}`}
              data-tracking-id="provide_email"
              data-tracking-ignore-value
              maxLength={255}
              name="email"
              type="email"
              autoComplete="email"
              inputClassName="text-gray-darker size-16 email-input"
              ariaDescribedBy={emailErrorFieldId}
              ref={inputRef}
              isValid={isValid}
              ariaInvalid={!isValid}
              required
              defaultValue={defaultEmailValue}
              placeholder="Enter email"
              className="mb-2 pos-r"
              aria-required="true"
              label="Email *"
              labelClassName="d-block size-12"
              onChange={onChange}
              onFocus={onEmailFormFieldFocus}
            />
            <FieldError id={emailErrorFieldId} error={emailError} classes="email-error pos-a mb-0" />
          </Col>
        </Row>
        <Button
          color="success"
          className="submit-btn size-16 text-capitalize py-0_5 d-flex align-items-center justify-content-center"
          disabled={isSubmitting || isSubmitted}
          onClick={handleSubmitEmail}
          aria-label={getStepCtaAriaLabel(stepCounter, nextStepTitle)}
          data-testid="submit-btn"
        >
          {isSubmitting && <Spinner size={16} color="white" className="me-0_5" />}
          {ctaText}
        </Button>
      </div>
      <EmailFormDisclaimer ctaText={ctaText} disclaimerSymbol="*" className="mt-1_5 size-12 cool-gray-40 mb-0" />
    </div>
  );
};

EmailStepUI.propTypes = {
  validationErrors: PropTypes.objectOf(PropTypes.string),
  validate: PropTypes.func.isRequired,
  fieldRef: PropTypes.func.isRequired,
  fields: PropTypes.shape({
    email: PropTypes.shape({
      setValue: PropTypes.func,
    }),
  }),
  isStepActive: PropTypes.bool,
  userEmail: PropTypes.string,
  updateIdmData: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  isEmailDataReady: PropTypes.bool,
  stepCounter: PropTypes.number.isRequired,
  nextStepTitle: PropTypes.string,
};

export const mapStateToProps = state => ({
  userEmail: state.profile?.data?.idm?.identifiers?.email?.[0],
  isAuthenticated: state.profile?.isAuthenticated,
});

export const EmailStepIdmWrapper = insiderMethods(EmailStepUI);

export const EmailStep = connect(mapStateToProps)(formValidation(EmailStepIdmWrapper, VALIDATORS));
